import { Flex, Form } from 'antd';
import OneTimePassword from '../form/one-time-password/one-time-password';
import { Button } from '@/components/button/button';
import VerificationMethod from './verification-method/verification-method';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './verification-form.module.scss';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/use-auth/use-auth';
import VerificationTimer from './verification-timer/verification-timer';

interface VerificationFormProps {
  userEmail: string;
  // userPhone: string;
  mfaToken: string;
  availableMethods: string[];
}

type VerificationFormFields = {
  oneTimePassword: string;
};

export default function VerificationForm({
  userEmail,
  // userPhone,
  mfaToken,
  availableMethods,
  ...props
}: VerificationFormProps) {
  const { t } = useTranslation(['auth', 'common']);
  const { state } = useLocation();
  const verificationMethod = state?.verificationMethod;
  const [isDisabled, setIsDisabled] = useState(true);
  const [codeResent, setCodeResent] = useState(false);

  const { verifyMFACode, isVerifyingMFACode, sendMFACode, isSendingMFACode } = useAuth();
  const navigate = useNavigate();

  // const anonymizedEmail = anonymizeEmail(userEmail);

  const handleOnFinish = async ({ oneTimePassword }: VerificationFormFields) => {
    try {
      await verifyMFACode({
        mfa_token: mfaToken,
        code: oneTimePassword,
      });
    } catch (error: any) {
      if (error?.response?.data?.code_resent) {
        setCodeResent(true);
      }
    }
  };

  const handleMethodSelected = (method: string) => {
    sendMFACode({
      mfa_token: mfaToken,
      method,
    });
    navigate('/sign-in', {
      state: {
        ...state,
        verification: true,
        verificationMethod: method,
        mfaToken,
        availableMethods,
      },
    });
  };

  const [form] = Form.useForm();

  return (
    <div className={styles.verificationWrapper}>
      {!verificationMethod ? (
        <VerificationMethod
          onMethodSelected={handleMethodSelected}
          isSendingMFACode={isSendingMFACode}
          // userEmail={anonymizedEmail}
          availableMethods={availableMethods}
        />
      ) : (
        <Flex vertical gap={8} {...props}>
          <h2>{t('auth:verification.code.title')}</h2>
          {/* <p>We sent a six-digit code to {anonymizedEmail}</p> */}
          <Form form={form} onFinish={handleOnFinish} layout="vertical">
            <Flex className={styles.oneTimePassword} justify="center" align="center">
              <Form.Item<VerificationFormFields>
                name="oneTimePassword"
                rules={[
                  {
                    required: true,
                    message: t('auth:verification.code.required'),
                  },
                ]}
              >
                <OneTimePassword
                  size="large"
                  onKeyDown={(e) => {
                    if (e.key === 'Backspace' || e.key === 'Delete' || e.key === ' ') {
                      setIsDisabled(true);
                    }
                  }}
                  onChange={(e) => {
                    if (e.match(/\W+/)) {
                      setIsDisabled(true);
                    } else {
                      setIsDisabled(false);
                    }
                  }}
                  autoFocus
                />
              </Form.Item>
            </Flex>
            <Form.Item>
              <Button
                htmlType="submit"
                size="large"
                type="primary"
                block
                disabled={isDisabled}
                loading={isVerifyingMFACode}
              >
                {t('common:actions.next')}
              </Button>
            </Form.Item>
          </Form>
          <VerificationTimer
            duration={180}
            codeResent={codeResent}
            onResendCode={() =>
              sendMFACode({
                mfa_token: mfaToken,
                method: verificationMethod,
              })
            }
          />
          <Flex vertical>
            <p>{t('auth:verification.code.changeMethodQuestion')}</p>

            <Flex align="center" className={styles.linkContainer}>
              <Button buttonVariant="link" onClick={() => navigate(-1)}>
                {t('auth:verification.code.changeMethod')}
              </Button>
            </Flex>
          </Flex>
        </Flex>
      )}
    </div>
  );
}
